import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, BarChart2, BookOpen, Globe, Activity, RefreshCw, Zap, Users } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { statsAPI } from '../services/api';
import './Home.css';

const FEATURES = [
  { Icon: Briefcase,  title: 'Opportunités en circulation',  desc: 'Emplois, stages et bourses qui circulent dans l\'écosystème — visibles en priorité pour ceux qui contribuent.' },
  { Icon: BarChart2,  title: 'Capital humain mesuré',        desc: 'L\'Alumni Impact Score™ quantifie la contribution réelle de chaque membre. Pas de CV, des actes.' },
  { Icon: BookOpen,   title: 'Transmission du savoir',       desc: 'Masterclasses animées par des alumni et des experts — le savoir circule, pas seulement l\'information.' },
  { Icon: Globe,      title: 'Diaspora active',              desc: 'Les Algériens établis à l\'étranger contribuent concrètement : mentorat, opportunités, transfert de compétences.' },
];

export default function Home() {
  const statsRef    = useScrollReveal();
  const featuresRef = useScrollReveal();
  const innovRef    = useScrollReveal();
  const ctaRef      = useScrollReveal();

  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    statsAPI.platform()
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch(() => { if (!cancelled) setStats(null); });
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="home">

      {/* ── Hero ───────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg-blobs" aria-hidden="true">
          <div className="hero-blob b1" />
          <div className="hero-blob b2" />
          <div className="hero-blob b3" />
        </div>
        <div
          className="hero-watermark"
          aria-hidden="true"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/logo/ynd-watermark-white.jpg)` }}
        />

        <div className="hero-inner">
          <div className="hero-logo-wrap hero-anim-1">
            <img
              src={`${process.env.PUBLIC_URL}/logo/ynd-primary-gold-navy.jpg`}
              alt="YesNeDni"
              className="hero-logo"
            />
          </div>

          <span className="hero-tag hero-anim-2">🇩🇿 Écosystème intelligent</span>

          <h1 className="hero-anim-3">
            L'infrastructure algérienne
            <em className="tagline-gold">de circulation du capital humain.</em>
          </h1>

          <p className="hero-tagline hero-anim-4">
            Your Next Door — Là où les opportunités rencontrent le potentiel.
          </p>

          <div className="hero-actions hero-anim-5">
            <Link to="/inscription"  className="btn-gold">Rejoindre gratuitement</Link>
            <Link to="/opportunites" className="btn-outline-hero">Explorer les opportunités</Link>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────── */}
      <section className={`stats-bar reveal${stats ? '' : ' stats-bar--hidden'}`} ref={statsRef}>
        {stats && (
          <>
            <div className="stat-item">
              <strong>{stats.mentoring_sessions_completed}</strong>
              <span>sessions de mentorat réalisées</span>
            </div>
            <div className="stat-item">
              <strong>{stats.verified_mentors}</strong>
              <span>mentors vérifiés</span>
            </div>
            <div className="stat-item">
              <strong>{stats.diaspora_members}</strong>
              <span>membres diaspora</span>
            </div>
            <div className="stat-item">
              <strong>{stats.active_masterclasses}</strong>
              <span>masterclasses & savoirs partagés</span>
            </div>
          </>
        )}
      </section>
      {stats && (
        <p className="stats-disclaimer">
          <span>
            Démonstration — Ces indicateurs illustrent le fonctionnement du modèle sur un jeu de données de
            démonstration. Les agrégats sont calculés en temps réel ; les visualisations individuelles sont
            anonymisées et aucune donnée personnelle n'est affichée.
          </span>
        </p>
      )}

      {/* ── Features ───────────────────────────────── */}
      <section className="features">
        <div className="section-inner reveal" ref={featuresRef}>
          <h2 className="section-title">Un écosystème, pas une plateforme</h2>
          <p className="section-sub">Trois flux en circulation permanente — opportunités, contribution et transmission du savoir.</p>
          <div className="features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">
                  <f.Icon size={38} strokeWidth={1.5} color="#E2B642" style={{ display: 'block', margin: '0 auto' }} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Architecture propriétaire ──────────────── */}
      <section className="innovation-section">
        <div className="section-inner reveal" ref={innovRef}>
          <div className="innovation-header">
            <span className="hero-tag inno-tag">Architecture propriétaire</span>
            <h2 className="section-title innovation-title">Une architecture, quatre mécanismes</h2>
            <p className="section-sub innovation-sub">
              L'intelligence de l'écosystème repose sur quatre mécanismes interconnectés qui produisent
              une capacité de mesure de l'impact humain inexistante ailleurs en Algérie.
            </p>
          </div>

          {/* Les trois mécanismes */}
          <div className="innovation-grid">

            <div className="inno-card inno-card--active">
              <div className="inno-top">
                <span className="inno-icon"><Activity size={26} strokeWidth={1.5} color="#E2B642" /></span>
                <span className="inno-status inno-status--live">v1 · actif</span>
              </div>
              <h3>Alumni Impact Score™</h3>
              <p>
                Mesure la contribution réelle de chaque membre selon 4 dimensions : régularité, validation
                communautaire, impact générationnel et réciprocité. Score 0–1000 recalculé en temps réel.
              </p>
              <div className="inno-formula">
                <span>Régularité <em>35 %</em></span>
                <span>Validation pair <em>30 %</em></span>
                <span>Impact générationnel <em>20 %</em></span>
                <span>Réciprocité <em>15 %</em></span>
              </div>
            </div>

            <div className="inno-card inno-card--active">
              <div className="inno-top">
                <span className="inno-icon"><Zap size={26} strokeWidth={1.5} color="#E2B642" /></span>
                <span className="inno-status inno-status--live">v1 · actif</span>
              </div>
              <h3>Matching sémantique</h3>
              <p>
                Moteur NLP léger (TF + similarité cosinus, 100 % natif) qui rapproche chaque profil des
                mentors les plus pertinents à partir du sens des textes — pas de mots-clés, du sens.
              </p>
            </div>

            <div className="inno-card inno-card--vision">
              <div className="inno-top">
                <span className="inno-icon"><RefreshCw size={26} strokeWidth={1.5} color="rgba(255,255,255,.55)" /></span>
                <span className="inno-status inno-status--partial">Moteur actif · intégration en cours</span>
              </div>
              <h3>Contribution Cycle Engine™</h3>
              <p>
                Cartographie les chaînes de transmission intergénérationnelle — l'étudiant d'aujourd'hui
                devient le mentor de demain, et le moteur mesure ces cycles d'impact sur le long terme.
              </p>
            </div>

            <div className="inno-card inno-card--active">
              <div className="inno-top">
                <span className="inno-icon"><Users size={26} strokeWidth={1.5} color="#E2B642" /></span>
                <span className="inno-status inno-status--live">v1 · actif</span>
              </div>
              <h3>Diaspora Contribution Index™</h3>
              <p>
                Mesure la contribution concrète des Algériens établis à l'étranger selon trois dimensions :
                formation transmise, savoir partagé et opportunités générées. Un indice composite et un taux
                de participation qui rendent visible l'engagement de la diaspora envers l'écosystème.
              </p>
              <div className="inno-formula">
                <span>Formation <em>40 %</em></span>
                <span>Partage <em>35 %</em></span>
                <span>Opportunités <em>25 %</em></span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── CTA ────────────────────────────────────── */}
      <section className="cta-section reveal" ref={ctaRef}>
        <div
          className="cta-watermark"
          aria-hidden="true"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/logo/ynd-watermark-white.jpg)` }}
        />
        <div className="section-inner cta-inner">
          <h2>Prêt à rejoindre l'écosystème ?</h2>
          <p>Rejoins la première infrastructure algérienne de circulation du capital humain.</p>
          <div className="hero-actions">
            <Link to="/inscription"  className="btn-gold">Créer mon compte</Link>
            <Link to="/opportunites" className="btn-outline-hero">Explorer les opportunités</Link>
          </div>
        </div>
      </section>

    </main>
  );
}
